import * as React from 'react'
import { useEffect, useState, startTransition } from 'react'

interface Props {
	children: React.ReactNode
}

/**
 * @component DeferredMount
 * @description Renders its children only after the first user interaction.
 * Used to keep below-fold, non-critical UI (e.g. the Footer) out of the initial
 * SSR DOM and off the initial hydration path, so it doesn't compete with the
 * LCP element for main-thread time on first load. SSR renders nothing (there is
 * no interaction on the server) and the client also renders nothing initially,
 * so hydration matches with no mismatch. This mirrors the below-fold gating in
 * B2BView, so deferred sections mount together on the same first interaction.
 */
const DeferredMount = ({ children }: Props) => {
	const [ready, setReady] = useState(false)

	useEffect(() => {
		let done = false
		const events: (keyof WindowEventMap)[] = ['scroll', 'pointerdown', 'pointermove', 'touchstart', 'wheel', 'keydown']

		const reveal = () => {
			if (done) return
			done = true
			events.forEach(e => window.removeEventListener(e, reveal))
			startTransition(() => setReady(true))
		}

		events.forEach(e => window.addEventListener(e, reveal, { passive: true }))

		return () => events.forEach(e => window.removeEventListener(e, reveal))
	}, [])

	return <>{ ready && children }</>
}

export default DeferredMount
